import React, { ReactNode, ReactElement } from 'react';
import { RatioQuestion, renderProblemContent } from './RatioQuestionsColored';
import { VocabRichText, ratioVocabSources } from './TextAnnotation';

/**
 * Recursively apply vocabulary annotations to a React tree
 * This walks through React elements and wraps text nodes with vocab highlighting
 */
function applyVocabToNode(node: ReactNode, keyPrefix: string = ''): ReactNode {
  if (typeof node === 'string') {
    // Apply vocab highlighting to string content
    return <VocabRichText text={node} sources={ratioVocabSources} />;
  }

  if (React.isValidElement(node)) {
    const element = node as ReactElement;

    // If the element has children, recursively process them
    if (element.props.children) {
      const children = React.Children.toArray(element.props.children);
      const processedChildren = children.map((child, idx) => {
        const processed = applyVocabToNode(child, `${keyPrefix}-${idx}`);
        // Ensure each processed child has a key when it's a valid React element
        if (React.isValidElement(processed) && !processed.key) {
          return React.cloneElement(processed, { key: `${keyPrefix}-child-${idx}` });
        }
        return processed;
      });

      // Clone the element with processed children, preserving the original key if it exists
      return React.cloneElement(element, { key: element.key || keyPrefix }, ...processedChildren);
    }

    return node;
  }

  // Arrays, fragments, etc.
  if (Array.isArray(node)) {
    return node.map((child, idx) => (
      <React.Fragment key={`${keyPrefix}-arr-${idx}`}>{applyVocabToNode(child, `${keyPrefix}-${idx}`)}</React.Fragment>
    ));
  }

  return node;
}

/**
 * Rich question text component that combines colored quantities with vocabulary tooltips
 */
export function RichQuestionText({ question }: { question: RatioQuestion }) {
  const { context, question: questionText } = renderProblemContent(question);

  // Apply vocabulary annotations to both context and question
  const contextWithVocab = applyVocabToNode(context, 'ctx');
  const questionWithVocab = applyVocabToNode(questionText, 'q');

  return (
    <>
      {/* Context statement with vocab tooltips AND colored quantities */}
      <div style={{ marginBottom: 'var(--spacing-4)' }}>{contextWithVocab}</div>

      {/* Question prompt with vocab tooltips and colored quantities */}
      <div>{questionWithVocab}</div>
    </>
  );
}
